import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

export default function AdminLoginPage() {
  const [etapa, setEtapa] = useState('credenciais'); // 'credenciais' | 'otp'
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const inputRefs = useRef([]);
  const { login, verificarOtp } = useAuth();
  const navigate = useNavigate();

  // ─── Passo 1: email + senha ───────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      const data = await login(email, senha);

      if (data?.requiresOtp) {
        setEmailOtp(data.email);
        setEtapa('otp');
        return;
      }

      if (data?.usuario?.role !== 'ADMIN') {
        setErro('Acesso negado. Apenas administradoras podem entrar aqui.');
        return;
      }

      navigate('/admin/dashboard', { replace: true });
    } catch {
      setErro('E-mail ou senha incorretos.');
    } finally {
      setCarregando(false);
    }
  };

  // ─── Passo 2: código OTP ──────────────────────────────────
  const handleDigit = (index, value) => {
    const v = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);
    if (v && index < 5) inputRefs.current[index + 1]?.focus();
    if (next.every(Boolean)) submitOtp(next.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
      submitOtp(pasted);
    }
  };

  const submitOtp = async (codigo) => {
    if (carregando) return;
    setErro('');
    setCarregando(true);
    try {
      const data = await verificarOtp(emailOtp, codigo);
      if (data?.usuario?.role === 'ADMIN') {
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.erro || 'Código incorreto.';
      setErro(msg);
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setCarregando(false);
    }
  };

  const voltarParaLogin = () => {
    setEtapa('credenciais');
    setDigits(['', '', '', '', '', '']);
    setErro('');
  };

  return (
    <div className="min-h-screen bg-[#fffafb] flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-xl border border-pink-100/60 p-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#ff2d6c] rounded-2xl flex items-center justify-center mx-auto text-white text-2xl font-black shadow-lg shadow-pink-500/25">
            L
          </div>
          <h1 className="text-xl font-black text-slate-900 mt-4 uppercase tracking-wide">
            Lily Admin
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            {etapa === 'credenciais'
              ? 'Área restrita'
              : 'Verificação de dois fatores'}
          </p>
        </div>

        {/* ─── PASSO 1: credenciais ─── */}
        {etapa === 'credenciais' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="admin@lilyclub.com"
                className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/20 focus:border-[#ff2d6c]"
              />
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Senha
              </label>
              <input
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/20 focus:border-[#ff2d6c]"
              />
            </div>

            {erro && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {erro}
              </p>
            )}

            <button
              type="submit"
              disabled={carregando}
              className="w-full bg-[#ff2d6c] text-white py-3.5 rounded-full font-black text-xs uppercase tracking-widest hover:bg-[#e02059] transition-all shadow-lg shadow-pink-500/20 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {carregando ? 'Verificando...' : 'Entrar no painel'}
            </button>
          </form>
        )}

        {/* ─── PASSO 2: código OTP ─── */}
        {etapa === 'otp' && (
          <div className="space-y-6">
            <div className="text-center space-y-1">
              <p className="text-sm text-slate-600 font-medium">
                Enviamos um código de 6 dígitos para
              </p>
              <p className="text-sm font-black text-slate-900">{emailOtp}</p>
              <p className="text-xs text-slate-400">Válido por 10 minutos</p>
            </div>

            {/* Inputs individuais por dígito */}
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleDigit(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  autoFocus={i === 0}
                  className="w-11 h-14 text-center text-2xl font-black text-slate-900 border-2 rounded-xl focus:outline-none focus:border-[#ff2d6c] transition-colors"
                  style={{ borderColor: d ? '#ff2d6c' : '#e2e8f0' }}
                />
              ))}
            </div>

            {carregando && (
              <div className="text-center text-xs text-slate-400 font-medium">
                Verificando código...
              </div>
            )}

            {erro && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-center">
                {erro}
              </p>
            )}

            <button
              type="button"
              onClick={voltarParaLogin}
              className="w-full text-xs text-slate-400 hover:text-[#ff2d6c] transition-colors font-medium py-1"
            >
              ← Voltar e tentar novamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
